import { ActionType, CompleteResourcesStepPayload } from "../../../AppDataProvider/actions";
import { AppContext, BookingData, Resource, ResourcePage } from "../../../AppDataProvider/AppDataProvider";
import { Button as BaseButton, LoadingPill as BaseLoadingPill, ListItem, NavButtons } from "../../../common/components";
import { CreateReservationMutationVariables, ResourceAllocationType, ResourceSelectionMode, useCreateReservationMutation } from "../../../GeneratedGraphQL";
import { getFirstStep, getReservationPartsInput } from "../../../utils";
import { marginBottom, marginTop, media, typography } from "../../../common/styles";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { applyOpacity } from "../../../common/styles/utils";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import GroupList from "./GroupList/GroupList";
import { PartRequired } from "../../../utils/types";
import { requireBookingDataFields } from "../../../utils";
import ResourceList from "./ResourceList/ResourceList";
import ResourcesByGroupList from "./ResourcesByGroupList/ResourcesByGroupList";
import { ServiceDuration } from "@src/shared/node/GeneratedDatabaseTypes";
import styled from "styled-components";
import { TimeSlot } from "../../../AppDataProvider/types";
import { useApolloClient } from "@apollo/client";
import { useDeleteExistingReservedSpace } from "../../Availability/hooks/useDeleteExistingReservedSpace";
import { useTranslation } from "react-i18next";
type StepContentProps = {
  resources: ResourcePage;
};
type ResourceStepBookingData = PartRequired<BookingData, "createdSource" | "organisation" | "service" | "timezone">;
export function StepContent(props: StepContentProps) {
  const {
    t
  } = useTranslation();
  const client = useApolloClient();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  const [createReservationLoading, setCreateReservationLoading] = useState<boolean>(false);
  const [autoCreatedReservation, setAutoCreatedReservation] = useState<boolean>(false);
  const [deleteReservationLoading, setDeleteReservationLoading] = useState<boolean>(false);
  const [createReservation] = useCreateReservationMutation({
    errorPolicy: "all"
  });
  requireBookingDataFields<ResourceStepBookingData>(appData.bookingData, ["createdSource", "organisation", "service", "timezone"]);
  const {
    bookingTool,
    bookingData
  } = appData;
  const {
    resourceSelectionMode
  } = bookingTool;
  const {
    createdSource,
    organisation,
    service,
    timezone,
    selectedTimeSlot,
    reservedSpaceId
  } = bookingData;
  const {
    resourceSelectionBeforeAvailability
  } = organisation;
  const {
    parts
  } = service;

  /**
   * If we have been provided a resourceId in the url params,
   * we want to automatically select that resource
   */
  const urlParams = new URLSearchParams(window.location.search);
  const preFillResourceId = urlParams.get("resourceId");
  const deleteExistingReservedSpace = useDeleteExistingReservedSpace({
    organisationId: organisation._id,
    reservedSpaceId
  });
  const isFirstStep = getFirstStep({
    bookingData: appData.bookingData,
    bookingTool: appData.bookingTool
  }) === "resources";
  const {
    resources
  } = props;

  /**
   * If there is only one resource, we want to
   * automatically select that resource
   */
  const hasSingleResource = resources.edges.length === 1;
  const preSelectedResourceId = preFillResourceId ? preFillResourceId : hasSingleResource ? resources.edges[0].node._id : null;
  const getListDisplay = (): JSX.Element => {
    const resourceNodes = resources.edges.map(({
      node
    }) => node);
    const resourceSelectionModeForDisplay = (resourceSelectionMode as ResourceSelectionMode.Group | ResourceSelectionMode.Resource | ResourceSelectionMode.ResourceByGroup);
    switch (resourceSelectionModeForDisplay) {
      case ResourceSelectionMode.Resource:
        return <ResourceList resources={resourceNodes} />;
      case ResourceSelectionMode.Group:
        return <GroupList resources={resourceNodes} />;
      case ResourceSelectionMode.ResourceByGroup:
        return <ResourcesByGroupList resources={resourceNodes} />;
    }
  };
  type CreateStandardReservationOpts = {
    attendees: number;
    duration: ServiceDuration;
    resource?: Resource;
    selectedTimeSlot: TimeSlot;
  };
  const createStandardReservation = useCallback(async ({
    attendees,
    duration,
    resource,
    selectedTimeSlot
  }: CreateStandardReservationOpts) => {
    const {
      interval
    } = selectedTimeSlot;

    /**
     * Add 1 minute to reservation expiry on the back end.
     * This buffer means front-end will prompt for a reload 1 minute before
     * actual expiry, avoiding situations where user is interacting at same
     * time as reservation expiry.
     * Chronos only accepts full-minute values so 1 minute is easiest compromise
     */
    const {
      reservationExpiryMinutes
    } = organisation;
    const expiresAfter = reservationExpiryMinutes + 1;
    const variables: CreateReservationMutationVariables = {
      expiresAfter,
      parts: getReservationPartsInput({
        parts,
        selectedInterval: interval
      }),
      requiredSpaces: attendees,
      serviceId: service._id,
      source: createdSource,
      timeZone: timezone,
      ...(resource && {
        resourceId: resource._id
      })
    };
    const {
      data,
      errors
    } = await createReservation({
      variables
    });
    if (errors?.some(error => error.message === "Response code 422 (Unprocessable Entity)")) {
      appDataDispatch({
        payload: {
          buttonLabel: t("reload"),
          content: t("errors.somethingChanged"),
          icon: faExclamationTriangle,
          onClick: /* istanbul ignore next */() => location.reload(),
          title: t("somethingChanged")
        },
        type: ActionType.SHOW_MODAL_DIALOG
      });
      return;
    } else if (!data || errors) {
      appDataDispatch({
        payload: {
          message: t("errors.cannotCreateReservation"),
          variant: "warning"
        },
        type: ActionType.ADD_TOAST
      });
      setCreateReservationLoading(false);
      return;
    }
    const {
      reservedSpace,
      reservation
    } = data.createReservation;
    setCreateReservationLoading(false);
    const updatedData = {
      attendees,
      duration,
      reservedSpaceId: reservedSpace._id,
      resource,
      startDate: new Date(reservation.start),
      timezone
    };
    const payload: CompleteResourcesStepPayload = {
      updatedData
    };
    appDataDispatch({
      payload,
      type: ActionType.COMPLETE_RESOURCES_STEP
    });
  }, [appDataDispatch, createReservation, createdSource, organisation, parts, service._id, t, timezone]);
  const selectAnyResource = () => {
    const {
      attendees,
      duration
    } = bookingData;
    if (attendees && duration && resourceSelectionMode !== ResourceSelectionMode.Disabled && service.resourceAllocationType === ResourceAllocationType.Any && selectedTimeSlot && !resourceSelectionBeforeAvailability) {
      const handleReservations = async () => {
        if (reservedSpaceId) {
          setDeleteReservationLoading(true);
          await deleteExistingReservedSpace();
          setDeleteReservationLoading(false);
        }
        setCreateReservationLoading(true);
        await createStandardReservation({
          attendees,
          duration,
          selectedTimeSlot
        });
      };
      void handleReservations();
      return;
    }
    appDataDispatch({
      payload: {
        updatedData: null
      },
      type: ActionType.COMPLETE_RESOURCES_STEP
    });
  };
  const goBack = () => {
    const goBackCleanup = async () => {
      if (reservedSpaceId) {
        setDeleteReservationLoading(true);
        await deleteExistingReservedSpace();
        setDeleteReservationLoading(false);
      }

      /**
       * Clear cache before leaving the step.
       * This prevents an issue when the previous step is availability
       * and cached query results can cause infinite re-renders.
       *
       * This appears to be a quirk of Apollo that i've not been able to
       * get to the bottom of yet.
       */
      await client.clearStore();
      appDataDispatch({
        type: ActionType.BACK_FROM_RESOURCES_STEP
      });
    };
    void goBackCleanup();
  };
  useEffect(() => {
    const {
      attendees,
      duration
    } = bookingData;
    if (!preSelectedResourceId || createReservationLoading || deleteReservationLoading || autoCreatedReservation || resources.edges.length === 0) {
      /**
       * To prevent multiple re-renders we need to make sure
       * we check if the reservation has already been created.
       *
       * As this useEffect runs multiple times during reservation creation
       */
      return;
    }
    setAutoCreatedReservation(true);
    const foundResource = resources.edges.find(({
      node
    }) => {
      return node._id === preSelectedResourceId;
    });
    if (!foundResource) {
      console.error(`Resource with ID ${preSelectedResourceId} not found.`);
      return;
    }
    if (attendees && duration && selectedTimeSlot && preSelectedResourceId && !resourceSelectionBeforeAvailability) {
      const handleReservations = async () => {
        setCreateReservationLoading(true);
        await createStandardReservation({
          attendees,
          duration,
          resource: foundResource.node,
          selectedTimeSlot
        });
      };
      void handleReservations();
      return;
    }
    const payload: CompleteResourcesStepPayload = {
      updatedData: {
        resource: foundResource.node
      }
    };
    appDataDispatch({
      payload,
      type: ActionType.COMPLETE_RESOURCES_STEP
    });
  }, [appDataDispatch, bookingData, createReservationLoading, createStandardReservation, deleteReservationLoading, preSelectedResourceId, resourceSelectionBeforeAvailability, resources.edges, selectedTimeSlot, reservedSpaceId, autoCreatedReservation]);
  return <>
      {organisation.allowSelectAnyResource && <>
          <SelectAny>
            <div>
              <SelectAnyName>{t("anyResourceName")}</SelectAnyName>
              <SelectAnyDescription>
                {t("anyResourceDescription")}
              </SelectAnyDescription>
            </div>
            <Button aria-label={t("selectAny")} onClick={selectAnyResource}>
              {t("select")}
            </Button>
          </SelectAny>
          <ChooseBelow>{t("orChooseBelow")}</ChooseBelow>
        </>}
      {(createReservationLoading || deleteReservationLoading || preSelectedResourceId) && <LoadingPill />}
      {
    /**
     * We want to hide the list of resources if
     * we have a pre filled resourceId or if there is only one resource
     */
    !preSelectedResourceId && getListDisplay()}

      {!isFirstStep && !preSelectedResourceId && <NavButtons hideContinue onBack={goBack} />}
    </>;
}
const SelectAny = styled(ListItem).attrs({
  as: "div"
})`
  ${marginBottom.m}
  @media ${media.mobile} {
    ${marginBottom.s}
  }
`;
const SelectAnyName = styled.h2`
  ${typography.subtitle}
  ${marginTop.zero}
  ${marginBottom.s}
  &::first-letter {
    text-transform: capitalize;
  }
`;
const SelectAnyDescription = styled.div`
  ${typography.body}
  @media ${media.mobile} {
    ${marginBottom.s}
  }
`;
const Button = styled(BaseButton)`
  min-width: 6.25rem;
  @media ${media.mobile} {
    width: 100%;
  }
`;
const ChooseBelow = styled.div`
  width: 100%;
  ${typography.body}
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)};
  ${marginBottom.l}
  @media ${media.mobile} {
    text-align: center;
  }
`;
const LoadingPill = styled(BaseLoadingPill)`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: absolute;
  justify-self: center;
  align-self: center;
`;
export default StepContent;